import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getOptimizedImage } from "./ResponsiveImage";
import { getRatgeberArticle, ratgeberArticles } from "../data/ratgeberArticles";
import {
  getArticleSeoGuidance,
  getGuidanceCluster,
  getGuidanceKeywords,
  getGuidanceLastReviewedAt,
} from "../data/articleSeoGuidance";
import { getTopicClusterBySchemaServiceId } from "../data/seoTopicClusters";

const SITE_URL = "https://en-power.eu";
const DEFAULT_IMAGE = `${SITE_URL}/assets/pv-dachflaeche-2026-06-01.jpg`;
const DEFAULT_IMAGE_ALT = "EN:POWER (ENPOWER) Energieprojekt mit Photovoltaik und Speicherlösungen";
const DEFAULT_IMAGE_WIDTH = 2592;
const DEFAULT_IMAGE_HEIGHT = 1727;
const HOME_HERO_IMAGE = "/assets/solar-roof-hero-1200.jpg";
const LOGO_URL = `${SITE_URL}/assets/ENPOWER-logo.png`;
const INDEX_ROBOTS = "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";

const businessId = `${SITE_URL}/#business`;
const websiteId = `${SITE_URL}/#website`;
const brandId = `${SITE_URL}/#brand`;
const editorialPersonId = `${SITE_URL}/#randy-kirsten`;
const brandAlternateNames = [
  "ENPOWER",
  "enpower",
  "en-power",
  "EN:POWER",
  "Kirsten und Klein eGbR Roofshare Solutions handelnd unter der Bezeichnung EN:POWER",
];

const pageMeta = {
  "/": {
    title: "EN:POWER | Energieprojekte aus Magdeburg für Deutschland",
    description:
      "EN:POWER aus Magdeburg entwickelt Dachverpachtung, Batteriespeicher, Kleinspeicher und Freiflächen-PV für Eigentümer von Dachflächen, Flächen und Grundstücken.",
    preloadImage: HOME_HERO_IMAGE,
  },
  "/leistungen": {
    title: "Leistungen | Dachfläche vermieten, Speicher und Freiflächen-PV",
    description:
      "Leistungen von EN:POWER: Dachfläche vermieten oder verpachten, Batteriespeicher, Kleinspeicher, Schwarmspeicher und Freiflächen-Photovoltaik prüfen lassen.",
  },
  "/ratgeber": {
    title: "Ratgeber | Dachfläche vermieten, Fläche verpachten und Speicher",
    description:
      "Ratgeber von EN:POWER zu Dachfläche vermieten, Dach verpachten, Grundstück oder Fläche verpachten, Batteriespeichern und Freiflächen-PV.",
  },
  "/pachtrechner": {
    title: "Pachtrechner | Potenzial für Dach, Speicher und Freifläche",
    description:
      "Mit dem EN:POWER Pachtrechner erhalten Eigentümer eine erste Einschätzung für Dachverpachtung, Batteriespeicher, Kleinspeicher oder Freiflächen-PV.",
  },
  "/ueber-uns": {
    title: "Über EN:POWER | Projektentwicklung für erneuerbare Energien",
    description:
      "EN:POWER ist Ihr Projektentwickler aus Magdeburg für erneuerbare Energien, Speicherprojekte, Dachverpachtung und Freiflächen-Photovoltaik.",
  },
  "/angebot-anfordern": {
    title: "Angebot anfordern | EN:POWER Energieprojekt prüfen lassen",
    description:
      "Kostenloses Angebot für Dachverpachtung, Batteriespeicher, Kleinspeicher, Schwarmspeicher oder Freiflächen-PV bei EN:POWER anfordern.",
  },
  "/impressum": {
    title: "Impressum | EN:POWER",
    description:
      "Impressum der Kirsten und Klein eGbR Roofshare Solutions, handelnd unter der Bezeichnung EN:POWER, Haferfeld 3, 06485 Quedlinburg.",
  },
  "/datenschutz": {
    title: "Datenschutz | EN:POWER",
    description:
      "Datenschutzerklärung von EN:POWER zur Verarbeitung personenbezogener Daten auf en-power.eu.",
  },
  "/agb": {
    title: "AGB | EN:POWER",
    description: "Allgemeine Geschäftsbedingungen von EN:POWER.",
    robots: "noindex, follow",
  },
};

const notFoundMeta = {
  title: "Seite nicht gefunden | EN:POWER",
  description:
    "Diese EN:POWER Seite wurde nicht gefunden. Nutzen Sie die Navigation, den Ratgeber oder die Anfragefunktion.",
  robots: "noindex, follow",
};

const services = [
  {
    id: "kleinspeicher-schwarmspeicher",
    name: "Kleinspeicher und Schwarmspeicher",
    url: `${SITE_URL}/leistungen#schwarmspeicher`,
    description:
      "Projektentwicklung für kleine Speicherflächen und dezentrale Speicherlösungen.",
  },
  {
    id: "batteriespeicher-bess",
    name: "Batteriespeicher (BESS)",
    url: `${SITE_URL}/leistungen#batteriespeicher`,
    description:
      "Projektentwicklung für große Batteriespeicher auf Gewerbe-, Industrie- und geeigneten Freiflächen.",
  },
  {
    id: "freiflaechen-photovoltaik",
    name: "Freiflächen-Photovoltaik",
    url: `${SITE_URL}/leistungen#freiflaechen`,
    description:
      "Entwicklung geeigneter Acker-, Grünland-, Brach-, Gewerbe- und Industrieflächen für Photovoltaik.",
  },
  {
    id: "dachverpachtung-photovoltaik",
    name: "Dachverpachtung für Photovoltaik",
    url: `${SITE_URL}/leistungen#dachverpachtung`,
    description:
      "Dachflächen als Einnahmequelle für Photovoltaik nutzen, inklusive Projektstrukturierung und Umsetzung.",
  },
];

const areaServed = [
  { "@type": "Country", name: "Deutschland" },
  { "@type": "AdministrativeArea", name: "Sachsen-Anhalt" },
  { "@type": "AdministrativeArea", name: "Sachsen" },
  { "@type": "AdministrativeArea", name: "Thüringen" },
  { "@type": "AdministrativeArea", name: "Niedersachsen" },
  { "@type": "AdministrativeArea", name: "Brandenburg" },
  { "@type": "City", name: "Magdeburg" },
];

const articleLocationSignals = [
  {
    token: "magdeburg",
    location: {
      "@type": "City",
      name: "Magdeburg",
      containedInPlace: { "@type": "AdministrativeArea", name: "Sachsen-Anhalt" },
    },
  },
  {
    token: "sachsen-anhalt",
    location: { "@type": "AdministrativeArea", name: "Sachsen-Anhalt" },
  },
  {
    token: "freistaat sachsen",
    location: { "@type": "AdministrativeArea", name: "Sachsen" },
  },
  {
    token: "thueringen",
    location: { "@type": "AdministrativeArea", name: "Thüringen" },
  },
  {
    token: "thüringen",
    location: { "@type": "AdministrativeArea", name: "Thüringen" },
  },
  {
    token: "niedersachsen",
    location: { "@type": "AdministrativeArea", name: "Niedersachsen" },
  },
  {
    token: "brandenburg",
    location: { "@type": "AdministrativeArea", name: "Brandenburg" },
  },
  {
    token: "mitteldeutschland",
    location: { "@type": "Place", name: "Mitteldeutschland" },
  },
  {
    token: "deutschland",
    location: { "@type": "Country", name: "Deutschland" },
  },
];

const getArticleSeoKeywords = (article) => getGuidanceKeywords(article);

const getArticleLocationCoverage = (article) => {
  const text = [
    article.slug,
    article.title,
    article.description,
    article.excerpt,
    ...getArticleSeoKeywords(article),
  ]
    .join(" ")
    .toLowerCase();

  const coverage = articleLocationSignals
    .filter(({ token }) => text.includes(token))
    .map(({ location }) => location);

  return coverage.length ? coverage : areaServed;
};

const getArticleFromPath = (pathname) => {
  const match = pathname.match(/^\/ratgeber\/([^/]+)$/);
  return match ? getRatgeberArticle(match[1]) : null;
};

const isKnownPath = (pathname) => Boolean(pageMeta[pathname] || getArticleFromPath(pathname));

const getPageConfig = (pathname) => {
  const article = getArticleFromPath(pathname);
  if (article) {
    return {
      title: article.seoTitle,
      description: article.description,
      image: `${SITE_URL}${article.image}`,
      imageAlt: article.imageAlt,
      imageWidth: article.imageWidth,
      imageHeight: article.imageHeight,
      preloadImage: `${SITE_URL}${article.image}`,
      article,
    };
  }

  if (!pageMeta[pathname]) {
    return {
      ...notFoundMeta,
      image: DEFAULT_IMAGE,
      imageAlt: DEFAULT_IMAGE_ALT,
      imageWidth: DEFAULT_IMAGE_WIDTH,
      imageHeight: DEFAULT_IMAGE_HEIGHT,
    };
  }

  return {
    ...pageMeta[pathname],
    image: DEFAULT_IMAGE,
    imageAlt: DEFAULT_IMAGE_ALT,
    imageWidth: DEFAULT_IMAGE_WIDTH,
    imageHeight: DEFAULT_IMAGE_HEIGHT,
  };
};

const getCanonicalPath = (pathname) => {
  if (isKnownPath(pathname)) {
    return pathname === "/" ? "/" : pathname;
  }
  return pathname;
};

const getCanonicalUrl = (pathname) => `${SITE_URL}${getCanonicalPath(pathname)}`;

const setMeta = (attribute, key, content) => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const removeMeta = (attribute, key) => {
  document.head
    .querySelectorAll(`meta[${attribute}="${key}"]`)
    .forEach((element) => element.remove());
};

const setMetaList = (attribute, key, values) => {
  removeMeta(attribute, key);
  values.forEach((value) => {
    const element = document.createElement("meta");
    element.setAttribute(attribute, key);
    element.setAttribute("content", value);
    document.head.appendChild(element);
  });
};

const setLink = (rel, href, extra = {}) => {
  let selector = `link[rel="${rel}"]`;
  if (extra.hreflang) {
    selector += `[hreflang="${extra.hreflang}"]`;
  }

  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    Object.entries(extra).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const getLocalAssetPath = (href) => {
  if (!href) return href;
  return href.startsWith(SITE_URL) ? href.slice(SITE_URL.length) : href;
};

const getPreloadImage = (href) => {
  const optimized = getOptimizedImage(getLocalAssetPath(href));

  if (!optimized) {
    return { href, attributes: {} };
  }

  const webpCandidates = optimized.webpSrcSet.split(",").map((candidate) => {
    const [path, width] = candidate.trim().split(/\s+/);
    return `${path} ${width}`;
  });

  return {
    href: webpCandidates[0].split(/\s+/)[0],
    attributes: {
      imagesrcset: webpCandidates.join(", "),
      imagesizes: optimized.sizes,
      type: "image/webp",
    },
  };
};

const setImagePreload = (href) => {
  const selector = 'link[rel="preload"][as="image"][data-enpower-route-preload="true"]';
  let element = document.head.querySelector(selector);

  if (!href) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "preload");
    element.setAttribute("as", "image");
    element.setAttribute("fetchpriority", "high");
    element.setAttribute("data-enpower-route-preload", "true");
    document.head.appendChild(element);
  }

  const preload = getPreloadImage(href);
  element.setAttribute("href", preload.href);
  ["imagesrcset", "imagesizes", "type"].forEach((attribute) => element.removeAttribute(attribute));
  Object.entries(preload.attributes).forEach(([attribute, value]) => {
    element.setAttribute(attribute, value);
  });
};

const getImageObject = (url, caption, width, height) => {
  const image = {
    "@type": "ImageObject",
    url,
    contentUrl: url,
    caption,
  };

  if (width && height) {
    image.width = width;
    image.height = height;
  }

  return image;
};

const getArticleWordCount = (article) => {
  const guidance = getArticleSeoGuidance(article.slug);
  const text = [
    article.title,
    article.excerpt,
    ...(article.sections || []).flatMap((section) => [
      section.heading,
      ...(section.paragraphs || []),
      ...(section.bullets || []),
    ]),
    guidance?.quickAnswer,
    ...(guidance?.decisionPoints || []),
    ...(article.faqs || []).flatMap((faq) => [faq.question, faq.answer]),
  ].join(" ");

  return text.split(/\s+/).filter(Boolean).length;
};

const getArticleBody = (article) => {
  const guidance = getArticleSeoGuidance(article.slug);

  return [
    article.title,
    article.excerpt,
    ...(article.sections || []).flatMap((section) => [
      section.heading,
      ...(section.paragraphs || []),
      ...(section.bullets || []),
    ]),
    guidance?.quickAnswer,
    ...(guidance?.decisionPoints || []),
    ...(article.faqs || []).flatMap((faq) => [faq.question, faq.answer]),
  ]
    .filter(Boolean)
    .join("\n\n");
};

const getReadingTimeDuration = (readingTime) => {
  const minutes = Number.parseInt(readingTime, 10);
  return Number.isFinite(minutes) ? `PT${minutes}M` : undefined;
};

const getServiceOffer = (service) => ({
  "@type": "Offer",
  "@id": `${SITE_URL}/#offer-${service.id}`,
  name: service.name,
  url: service.url,
  areaServed,
  itemOffered: { "@id": `${SITE_URL}/#service-${service.id}` },
});

const getStructuredData = (pathname) => {
  const page = getPageConfig(pathname);
  const canonicalUrl = getCanonicalUrl(pathname);
  const pageImage = page.image || DEFAULT_IMAGE;
  const pageImageAlt = page.imageAlt || DEFAULT_IMAGE_ALT;
  const pageImageWidth = page.imageWidth || DEFAULT_IMAGE_WIDTH;
  const pageImageHeight = page.imageHeight || DEFAULT_IMAGE_HEIGHT;
  const articleGuidance = page.article ? getArticleSeoGuidance(page.article.slug) : null;
  const articleCluster = page.article ? getGuidanceCluster(page.article.slug) : null;
  const articleModifiedAt = page.article ? getGuidanceLastReviewedAt(page.article) : undefined;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["LocalBusiness", "ProfessionalService"],
        "@id": businessId,
        name: "EN:POWER",
        legalName: "Kirsten und Klein eGbR Roofshare Solutions",
        alternateName: brandAlternateNames,
        url: SITE_URL,
        logo: LOGO_URL,
        brand: { "@id": brandId },
        image: [
          DEFAULT_IMAGE,
          `${SITE_URL}/assets/bess-grossspeicher-scalecube-i3-2026-06-01.png`,
          `${SITE_URL}/assets/kleinspeicher-schwarmspeicher-2026-06-01.jpeg`,
        ],
        description:
          "EN:POWER entwickelt Projekte für Dachverpachtung, Batteriespeicher, Kleinspeicher, Schwarmspeicher und Freiflächen-Photovoltaik.",
        telephone: "+49 160 8732681",
        email: "info@en-power.eu",
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "Kundenanfragen",
          telephone: "+49 160 8732681",
          email: "info@en-power.eu",
          availableLanguage: ["de"],
          areaServed,
        },
        address: {
          "@type": "PostalAddress",
          streetAddress: "Haferfeld 3",
          postalCode: "06485",
          addressLocality: "Quedlinburg",
          addressRegion: "Sachsen-Anhalt",
          addressCountry: "DE",
        },
        areaServed,
        knowsAbout: [
          "Dachverpachtung",
          "Photovoltaik",
          "Batteriespeicher",
          "BESS",
          "Kleinspeicher",
          "Schwarmspeicher",
          "Freiflächen-Photovoltaik",
          "Flaeche verpachten",
          "Flaeche vermieten",
          "Grundstueck verpachten",
          "Grundstueck vermieten",
          "Land verpachten",
          "Solarpark Flaeche",
          "Batteriespeicher Flaeche verpachten",
          "Dachflaeche vermieten Photovoltaik",
          "Dach vermieten Solar",
          "Dachflaeche fuer Solar vermieten",
          "Dachflaeche verpachten",
          "Freiflaeche fuer Photovoltaik verpachten",
          "Flaeche fuer Photovoltaik verpachten",
          "Mitteldeutschland",
          "Sachsen",
          "Thueringen",
          "Niedersachsen",
          "Brandenburg",
          "Sachsen-Anhalt",
          "Projektentwicklung erneuerbare Energien",
        ],
        makesOffer: services.map((service) => ({
          "@id": `${SITE_URL}/#offer-${service.id}`,
        })),
        hasOfferCatalog: { "@id": `${SITE_URL}/#offer-catalog` },
      },
      {
        "@type": "Brand",
        "@id": brandId,
        name: "EN:POWER",
        alternateName: brandAlternateNames,
        url: SITE_URL,
        logo: LOGO_URL,
      },
      {
        "@type": "Person",
        "@id": editorialPersonId,
        name: "Randy Wesley Kirsten",
        jobTitle: "Vertretungsberechtigter Gesellschafter",
        url: `${SITE_URL}/impressum`,
        worksFor: { "@id": businessId },
      },
      {
        "@type": "WebSite",
        "@id": websiteId,
        url: SITE_URL,
        name: "EN:POWER",
        alternateName: ["ENPOWER", "enpower", "en-power"],
        inLanguage: "de-DE",
        publisher: { "@id": businessId },
      },
      ...services.map((service) => ({
        "@type": "Service",
        "@id": `${SITE_URL}/#service-${service.id}`,
        name: service.name,
        description: service.description,
        url: service.url,
        provider: { "@id": businessId },
        areaServed,
        serviceType: service.name,
        audience: {
          "@type": "Audience",
          audienceType: "Dach-, Fl\u00e4chen- und Grundst\u00fcckseigent\u00fcmer",
        },
        keywords: getTopicClusterBySchemaServiceId(service.id)?.keywords?.join(", "),
        subjectOf: getTopicClusterBySchemaServiceId(service.id)?.articleSlugs?.map((slug) => ({
          "@id": `${SITE_URL}/ratgeber/${slug}#article`,
        })),
      })),
      {
        "@type": "OfferCatalog",
        "@id": `${SITE_URL}/#offer-catalog`,
        name: "EN:POWER Leistungsangebot",
        itemListElement: services.map((service) => getServiceOffer(service)),
      },
      {
        "@type": "Blog",
        "@id": `${SITE_URL}/ratgeber#blog`,
        url: `${SITE_URL}/ratgeber`,
        name: "EN:POWER Ratgeber",
        description: pageMeta["/ratgeber"].description,
        inLanguage: "de-DE",
        publisher: { "@id": businessId },
        blogPost: ratgeberArticles.map((article) => ({
          "@id": `${SITE_URL}/ratgeber/${article.slug}#article`,
        })),
      },
      {
        "@type": "ItemList",
        "@id": `${SITE_URL}/ratgeber#itemlist`,
        name: "Ratgeberartikel von EN:POWER",
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        numberOfItems: ratgeberArticles.length,
        itemListElement: ratgeberArticles.map((article, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE_URL}/ratgeber/${article.slug}`,
          name: article.title,
        })),
      },
      {
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: page.title,
        description: page.description,
        inLanguage: "de-DE",
        isPartOf: { "@id": websiteId },
        about: { "@id": businessId },
        primaryImageOfPage: {
          ...getImageObject(pageImage, pageImageAlt, pageImageWidth, pageImageHeight),
        },
        ...(page.article ? { mainEntity: { "@id": `${canonicalUrl}#article` } } : {}),
      },
      ...(page.article
        ? [
            {
              "@type": "Article",
              "@id": `${canonicalUrl}#article`,
              headline: page.article.title,
              description: page.article.description,
              abstract: articleGuidance?.quickAnswer,
              image: getImageObject(pageImage, pageImageAlt, pageImageWidth, pageImageHeight),
              datePublished: page.article.publishedAt,
              dateModified: articleModifiedAt,
              inLanguage: "de-DE",
              author: { "@id": businessId },
              editor: { "@id": editorialPersonId },
              reviewedBy: { "@id": editorialPersonId },
              isPartOf: { "@id": `${SITE_URL}/ratgeber#blog` },
              publisher: { "@id": businessId },
              mainEntityOfPage: { "@id": `${canonicalUrl}#webpage` },
              keywords: getArticleSeoKeywords(page.article).join(", "),
              articleSection: page.article.category,
              wordCount: getArticleWordCount(page.article),
              timeRequired: getReadingTimeDuration(page.article.readingTime),
              articleBody: getArticleBody(page.article),
              spatialCoverage: getArticleLocationCoverage(page.article),
              contentLocation: getArticleLocationCoverage(page.article),
              about: getArticleSeoKeywords(page.article).map((keyword) => ({
                "@type": "Thing",
                name: keyword,
              })),
              mentions: [
                ...(articleCluster
                  ? [{ "@id": `${SITE_URL}/#service-${articleCluster.schemaServiceId}` }]
                  : []),
                ...(articleGuidance?.secondaryKeywords || []).map((keyword) => ({
                  "@type": "Thing",
                  name: keyword,
                })),
              ],
            },
          ]
        : []),
      {
        "@type": "BreadcrumbList",
        "@id": `${canonicalUrl}#breadcrumb`,
        itemListElement: page.article
          ? [
              {
                "@type": "ListItem",
                position: 1,
                name: "EN:POWER",
                item: `${SITE_URL}/`,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Ratgeber",
                item: `${SITE_URL}/ratgeber`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: page.article.title,
                item: canonicalUrl,
              },
            ]
          : [
              {
                "@type": "ListItem",
                position: 1,
                name: "EN:POWER",
                item: `${SITE_URL}/`,
              },
              ...(getCanonicalPath(pathname) === "/"
                ? []
                : [
                    {
                      "@type": "ListItem",
                      position: 2,
                      name: page.title.split("|")[0].trim(),
                      item: canonicalUrl,
                    },
                  ]),
            ],
      },
    ],
  };
};

export const Seo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const page = getPageConfig(pathname);
    const canonicalUrl = getCanonicalUrl(pathname);

    document.title = page.title;

    setMeta("name", "description", page.description);
    setMeta("name", "robots", page.robots || INDEX_ROBOTS);
    setMeta("property", "og:title", page.title);
    setMeta("property", "og:description", page.description);
    setMeta("property", "og:type", page.article ? "article" : "website");
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:site_name", "EN:POWER");
    setMeta("property", "og:locale", "de_DE");
    setMeta("property", "og:image", page.image || DEFAULT_IMAGE);
    setMeta("property", "og:image:width", String(page.imageWidth || DEFAULT_IMAGE_WIDTH));
    setMeta("property", "og:image:height", String(page.imageHeight || DEFAULT_IMAGE_HEIGHT));
    setMeta("property", "og:image:alt", page.imageAlt || DEFAULT_IMAGE_ALT);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", page.title);
    setMeta("name", "twitter:description", page.description);
    setMeta("name", "twitter:image", page.image || DEFAULT_IMAGE);
    setMeta("name", "twitter:image:alt", page.imageAlt || DEFAULT_IMAGE_ALT);

    if (page.article) {
      setMeta("property", "article:published_time", page.article.publishedAt);
      setMeta("property", "article:modified_time", getGuidanceLastReviewedAt(page.article));
      setMeta("property", "article:section", page.article.category);
      setMetaList("property", "article:tag", getArticleSeoKeywords(page.article));
    } else {
      removeMeta("property", "article:published_time");
      removeMeta("property", "article:modified_time");
      removeMeta("property", "article:section");
      removeMeta("property", "article:tag");
    }

    setLink("canonical", canonicalUrl);
    setLink("alternate", canonicalUrl, { hreflang: "de-DE" });
    setLink("alternate", canonicalUrl, { hreflang: "x-default" });
    setImagePreload(page.preloadImage);

    let structuredData = document.head.querySelector("#enpower-structured-data");
    if (!structuredData) {
      structuredData = document.createElement("script");
      structuredData.id = "enpower-structured-data";
      structuredData.type = "application/ld+json";
      document.head.appendChild(structuredData);
    }
    structuredData.textContent = JSON.stringify(getStructuredData(pathname));
  }, [pathname]);

  return null;
};
